import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Database, HardDrive, RefreshCw, ShieldCheck, AlertTriangle, Files } from "lucide-react";
import { formatDateTime } from "../lib/format";

const TONE = {
    ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warning: "bg-amber-50 text-amber-900 border-amber-200",
    critical: "bg-rose-50 text-rose-800 border-rose-200",
};

function Stat({ label, value, sub, testid }) {
    return (
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
            <div className="num font-bold text-slate-900 mt-0.5" data-testid={testid}>{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
    );
}

export function StorageHealthCard() {
    const [h, setH] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    async function load() {
        setBusy(true); setErr("");
        try { setH((await api.get("/admin/storage-health")).data); }
        catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    }
    useEffect(() => { load(); }, []);

    const pct = h ? Math.min(100, Math.round((h.database.data_mb / h.database.critical_mb) * 100)) : 0;
    return (
        <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-4" data-testid="storage-health-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-blue-900" />
                    <h2 className="font-display text-lg font-bold text-slate-900">Storage &amp; Database Health</h2>
                    {h && (
                        <span className={`text-[10px] uppercase tracking-wide font-semibold border rounded-md px-2 py-0.5 ${TONE[h.status]}`}
                            data-testid="storage-health-status">{h.status}</span>
                    )}
                </div>
                <Button variant="outline" size="sm" className="rounded-xl" onClick={load} disabled={busy} data-testid="storage-health-refresh">
                    <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
                </Button>
            </div>
            {err && <p className="text-sm text-rose-700">{err}</p>}
            {!h && !err && <p className="text-sm text-slate-400">Loading…</p>}
            {h && (
                <>
                    {h.warnings.length > 0 ? (
                        <ul className="space-y-1.5" data-testid="storage-warnings">
                            {h.warnings.map((w) => (
                                <li key={w.code} className={`flex items-start gap-2 text-sm rounded-xl border px-3 py-2 ${TONE[w.level]}`}>
                                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {w.message}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className={`flex items-center gap-2 text-sm rounded-xl border px-3 py-2 ${TONE.ok}`} data-testid="storage-no-warnings">
                            <ShieldCheck className="h-4 w-4" /> No storage or backup issues detected.
                        </div>
                    )}
                    <div>
                        <div className="flex justify-between text-xs text-slate-600 mb-1">
                            <span>Database size {h.database.data_mb} MB of {h.database.critical_mb} MB limit</span>
                            <span>{pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className={`h-full ${pct >= 95 ? "bg-rose-600" : pct >= 80 ? "bg-amber-500" : "bg-blue-900"}`} style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <Stat label="Records" value={h.database.objects.toLocaleString("en-IN")} sub={`${h.database.collections} collections`} testid="storage-objects" />
                        <Stat label="Data + Indexes" value={`${h.database.total_mb} MB`} sub={`indexes ${h.database.index_mb} MB`} />
                        <Stat label="Growth (7 days)" value={h.growth.last_7_days_pct == null ? "—" : `${h.growth.last_7_days_pct}%`} sub={h.growth.since_yesterday_pct == null ? "no history yet" : `${h.growth.since_yesterday_pct}% since yesterday`} />
                        <Stat label="Server disk" value={`${h.disk.used_pct}% used`} sub={`${h.disk.free_mb} MB free`} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div className="rounded-xl border border-slate-100 p-3 text-sm space-y-1">
                            <div className="flex items-center gap-1.5 font-semibold text-slate-800"><HardDrive className="h-4 w-4 text-blue-900" /> Backups</div>
                            <div className="text-xs text-slate-600" data-testid="storage-last-backup">
                                Last: {h.backups.last_backup_at ? `${formatDateTime(h.backups.last_backup_at)} (${h.backups.last_backup_kind})` : "never"}
                            </div>
                            <div className="text-xs text-slate-600">Daily job: {h.backups.last_daily_at ? formatDateTime(h.backups.last_daily_at) : "not run yet"} · kept {h.backups.retention_days_daily} days</div>
                            <div className="text-xs text-slate-600">{h.backups.total_count} snapshots · {h.backups.total_mb} MB · {h.backups.storage} · SHA-256 verified</div>
                        </div>
                        <div className="rounded-xl border border-slate-100 p-3 text-sm space-y-1">
                            <div className="flex items-center gap-1.5 font-semibold text-slate-800"><Files className="h-4 w-4 text-blue-900" /> Files (KYC / PDFs)</div>
                            <div className="text-xs text-slate-600">Backend: <b>{h.files.backend}</b> · KYC inline {h.files.kyc_inline_mb} MB across {h.files.customers_with_files}/{h.files.customers} customers</div>
                            <div className="text-xs text-slate-600">PDFs: {h.files.pdfs}</div>
                        </div>
                    </div>
                    <details className="text-xs text-slate-600">
                        <summary className="cursor-pointer font-semibold text-slate-700">Per-collection sizes</summary>
                        <table className="w-full mt-2">
                            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500"><th>Collection</th><th className="text-right">Docs</th><th className="text-right">Data MB</th><th className="text-right">Idx MB</th><th className="text-right">Avg KB</th></tr></thead>
                            <tbody>
                                {Object.entries(h.collections).map(([k, v]) => (
                                    <tr key={k} className="border-t border-slate-100"><td className="py-1">{k}</td><td className="text-right num">{v.count.toLocaleString("en-IN")}</td><td className="text-right num">{v.data_mb}</td><td className="text-right num">{v.index_mb}</td><td className="text-right num">{v.avg_obj_kb}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    </details>
                </>
            )}
        </Card>
    );
}
